// externals
import { apiConfigHelper, locHrefUtil } from '@makemydeal/dr-shared-ui-utils';
import { RSAA } from 'redux-api-middleware';
import {
    TRADE_IN_YEARS_REQUEST,
    TRADE_IN_YEARS_SUCCESS,
    TRADE_IN_YEARS_FAILURE,
    TRADE_IN_MAKES_REQUEST,
    TRADE_IN_MAKES_SUCCESS,
    TRADE_IN_MAKES_FAILURE,
    TRADE_IN_MODELS_REQUEST,
    TRADE_IN_MODELS_SUCCESS,
    TRADE_IN_MODELS_FAILURE,
    TRADE_IN_TRIMS_REQUEST,
    TRADE_IN_TRIMS_SUCCESS,
    TRADE_IN_TRIMS_FAILURE,
    TRADE_IN_VEHICLES_REQUEST,
    TRADE_IN_VEHICLES_SUCCESS,
    TRADE_IN_VEHICLES_FAILURE,
    TRADE_IN_OPTIONS_REQUEST,
    TRADE_IN_OPTIONS_SUCCESS,
    TRADE_IN_OPTIONS_FAILURE,
    TRADE_IN_VALIDATE_OPTIONS_REQUEST,
    TRADE_IN_VALIDATE_OPTIONS_SUCCESS,
    TRADE_IN_VALIDATE_OPTIONS_FAILURE
} from '../actionTypes/tradeInComponentActionTypes';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { Actions as types } from '@makemydeal/dr-activities-tradein';

import * as tradeInComponentSelectors from '../selectors/tradeInComponentSelectors';
import { getTradeInComponentYear } from '../selectors/tradeInComponentSelectors';
import {
    TRADE_IN_ESTIMATION_FAILURE,
    TRADE_IN_ESTIMATION_REQUEST,
    TRADE_IN_ESTIMATION_SUCCESS,
    TRADE_IN_REMOVE_FAILURE,
    TRADE_IN_REMOVE_REQUEST,
    TRADE_IN_REMOVE_SUCCESS,
    TRADE_IN_UPDATE_FAILURE,
    TRADE_IN_UPDATE_REQUEST,
    TRADE_IN_UPDATE_SUCCESS
} from '../actionTypes/tradeActionTypes';
import type { StateTree } from '@makemydeal/dr-dash-types';

export const fetchTradeInComponentYears = (appPrefix: string) => {
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(appPrefix, locHref, '/trade');
    const endpoint = bffApiUrlResult.url + '/lookup/years?vehicleClass=used';

    return {
        [RSAA]: {
            endpoint,
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            types: [TRADE_IN_YEARS_REQUEST, TRADE_IN_YEARS_SUCCESS, TRADE_IN_YEARS_FAILURE]
        }
    };
};

export const fetchTradeInComponentMakes = (appPrefix: string, year: string) => {
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(appPrefix, locHref, '/trade');
    const searchParams = {
        year,
        vehicleClass: 'used'
    };
    const queryString = new URLSearchParams(searchParams).toString();
    const endpoint = bffApiUrlResult.url + `/lookup/makes?${queryString}`;

    return {
        [RSAA]: {
            endpoint,
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            types: [TRADE_IN_MAKES_REQUEST, TRADE_IN_MAKES_SUCCESS, TRADE_IN_MAKES_FAILURE]
        }
    };
};

export const fetchTradeInComponentModels = (appPrefix: string, year: string, id: string) => {
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(appPrefix, locHref, '/trade');
    const searchParams = {
        year,
        makeid: id,
        vehicleClass: 'used'
    };
    const queryString = new URLSearchParams(searchParams).toString();
    const endpoint = bffApiUrlResult.url + `/lookup/models?${queryString}`;

    return {
        [RSAA]: {
            endpoint,
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            types: [TRADE_IN_MODELS_REQUEST, TRADE_IN_MODELS_SUCCESS, TRADE_IN_MODELS_FAILURE]
        }
    };
};

export const fetchTradeInComponentTrims = (appPrefix: string, year: string, makeId: string, modelId: string) => {
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(appPrefix, locHref, '/trade');
    const searchParams = {
        year,
        makeid: makeId,
        modelid: modelId,
        vehicleClass: 'used'
    };
    const queryString = new URLSearchParams(searchParams).toString();
    const endpoint = bffApiUrlResult.url + `/lookup/trims?${queryString}`;

    return {
        [RSAA]: {
            endpoint,
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            types: [TRADE_IN_TRIMS_REQUEST, TRADE_IN_TRIMS_SUCCESS, TRADE_IN_TRIMS_FAILURE]
        }
    };
};

export const fetchTradeInComponentVehicle = (appPrefix: string, year: string, makeId: string, modelId: string, trimId: string) => {
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(appPrefix, locHref, '/trade');
    const searchParams = {
        year,
        makeid: makeId,
        modelid: modelId,
        trimid: trimId
    };
    const queryString = new URLSearchParams(searchParams).toString();
    const endpoint = bffApiUrlResult.url + `/lookup/vehicles?${queryString}`;

    return {
        [RSAA]: {
            endpoint,
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            types: [TRADE_IN_VEHICLES_REQUEST, TRADE_IN_VEHICLES_SUCCESS, TRADE_IN_VEHICLES_FAILURE]
        }
    };
};

export const fetchTradeInVehicleOptions = (
    appPrefix: string,
    year: string,
    makeId: string,
    modelId: string,
    vehicleId: string,
    zip: string,
    trimId: string
) => {
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(appPrefix, locHref, '/trade');
    const searchParams = {
        year,
        makeid: makeId,
        modelid: modelId,
        vehicleId,
        zip,
        trimid: trimId
    };

    const queryString = new URLSearchParams(searchParams).toString();
    const endpoint = bffApiUrlResult.url + `/lookup/options?${queryString}`;

    return {
        [RSAA]: {
            endpoint,
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            types: [TRADE_IN_OPTIONS_REQUEST, TRADE_IN_OPTIONS_SUCCESS, TRADE_IN_OPTIONS_FAILURE]
        }
    };
};

export const validateTradeInOptions = (appPrefix: string, tradeIn: StateTree) => {
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(appPrefix, locHref, '/trade');
    const endpoint = bffApiUrlResult.url + '/validate/options';
    const payload = JSON.stringify({
        vehicleId: tradeInComponentSelectors.getTradeInComponentVehicle(tradeIn).trim.vehicleId,
        year: `${getTradeInComponentYear(tradeIn)}`,
        makeId: tradeInComponentSelectors.getTradeInComponentVehicle(tradeIn).make.id,
        modelId: tradeInComponentSelectors.getTradeInComponentVehicle(tradeIn).model.id,
        mileage: `${tradeInComponentSelectors.getTradeInComponentVehicleMileage(tradeIn)}`,
        trimId: tradeInComponentSelectors.getTradeInComponentVehicle(tradeIn).trim.id,
        zip: `${tradeInComponentSelectors.getTradeInComponent(tradeIn).zip}`,
        vehicleClass: 'used',
        condition: tradeInComponentSelectors.getTradeInComponentCondition(tradeIn),
        vehicleOptions: tradeInComponentSelectors.getTradeInComponent(tradeIn).vehicleOptions
    });

    return {
        [RSAA]: {
            endpoint,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            types: [TRADE_IN_VALIDATE_OPTIONS_REQUEST, TRADE_IN_VALIDATE_OPTIONS_SUCCESS, TRADE_IN_VALIDATE_OPTIONS_FAILURE]
        }
    };
};

export function performTradeInValuation(tradeIn: StateTree) {
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl('sales', locHref, '/trade');
    const endpoint = bffApiUrlResult.url + `/estimate?`;

    const estimationPayload = JSON.stringify({
        vehicleId: tradeInComponentSelectors.getTradeInComponentVehicle(tradeIn).trim.vehicleId,
        mileage: `${tradeInComponentSelectors.getTradeInComponentVehicleMileage(tradeIn)}`,
        zip: `${tradeInComponentSelectors.getTradeInComponent(tradeIn).zip}`,
        vehicleClass: 'used',
        selectedCondition: tradeInComponentSelectors.getTradeInComponentCondition(tradeIn),
        vehicleOptions: tradeInComponentSelectors.getTradeInComponent(tradeIn).vehicleOptions,
        yearId: `${getTradeInComponentYear(tradeIn)}`,
        modelId: tradeInComponentSelectors.getTradeInComponentVehicle(tradeIn).model.id,
        makeId: tradeInComponentSelectors.getTradeInComponentVehicle(tradeIn).make.id,
        trimId: tradeInComponentSelectors.getTradeInComponentVehicle(tradeIn).trim.id
    });

    return {
        [RSAA]: {
            endpoint,
            body: estimationPayload,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            types: [TRADE_IN_ESTIMATION_REQUEST, TRADE_IN_ESTIMATION_SUCCESS, TRADE_IN_ESTIMATION_FAILURE]
        }
    };
}
export const callSubaruEligibilitySummaryCheckEndpoint = (vin: string, mileage: number, dealerId: number) => {
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl('sales', locHref, '/');
    const endpoint = bffApiUrlResult.url + `/gtp-trade/v2/eligible/${vin}?mileage=${mileage}`;

    const meta = {
        originalParams: {
            vin,
            mileage
        }
    };

    return {
        [RSAA]: {
            endpoint: endpoint,
            method: 'GET',
            headers: {
                Accept: 'application/json',
                dealerId
            },
            types: [
                {
                    type: types.SUBARU_ELIGIBILITY_CHECK_REQUEST,

                    meta: (_action: AnyFSA, _state: any, _res: any) => {
                        return meta;
                    }
                },
                {
                    type: types.SUBARU_ELIGIBILITY_SUMMARY_CHECK_SUCCESS,

                    meta: (_action: AnyFSA, _state: any, _res: any) => {
                        return meta;
                    }
                },
                {
                    type: types.SUBARU_ELIGIBILITY_SUMMARY_CHECK_FAILURE,

                    meta: (_action: AnyFSA, _state: any, _res: any) => {
                        return meta;
                    }
                }
            ]
        }
    };
};

export const callSubaruEligibilityCheckEndpoint = (
    vin: string,
    mileage: number,
    dealerId: number,
    fallbackAction?: () => void,
    isUniqueEstimateOption?: boolean
) => {
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl('sales', locHref, '/');
    const endpoint = bffApiUrlResult.url + `/gtp-trade/v2/eligible/${vin}?mileage=${mileage}`;

    const meta = {
        originalParams: {
            vin,
            mileage,
            fallbackAction,
            isUniqueEstimateOption
        }
    };

    return {
        [RSAA]: {
            endpoint: endpoint,
            method: 'GET',
            headers: {
                Accept: 'application/json',
                dealerId
            },
            types: [
                {
                    type: types.SUBARU_ELIGIBILITY_CHECK_REQUEST,

                    meta: (_action: AnyFSA, _state: any, _res: any) => {
                        return meta;
                    }
                },
                {
                    type: types.SUBARU_ELIGIBILITY_CHECK_SUCCESS,

                    meta: (_action: AnyFSA, _state: any, _res: any) => {
                        return meta;
                    }
                },
                {
                    type: types.SUBARU_ELIGIBILITY_CHECK_FAILURE,

                    meta: (_action: AnyFSA, _state: any, _res: any) => {
                        return meta;
                    }
                }
            ]
        }
    };
};

export const updateSCITradeIn = (appPrefix: string, payload: any) => {
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(appPrefix, locHref, '/trade');
    const endpoint = bffApiUrlResult.url + '/update';
    return {
        [RSAA]: {
            endpoint,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...payload, type: 'UPDATE_TRADEIN' }),
            types: [TRADE_IN_UPDATE_REQUEST, TRADE_IN_UPDATE_SUCCESS, TRADE_IN_UPDATE_FAILURE]
        }
    };
};

export const removeSCITradeIn = (appPrefix: string, payload: any) => {
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(appPrefix, locHref, '/trade');
    const endpoint = bffApiUrlResult.url + '/remove';
    return {
        [RSAA]: {
            endpoint,
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...payload, type: 'REMOVE_TRADEIN' }),
            types: [TRADE_IN_REMOVE_REQUEST, TRADE_IN_REMOVE_SUCCESS, TRADE_IN_REMOVE_FAILURE]
        }
    };
};
